import { useCallback } from "react";
import useHttp from "./useHttp";

function parseFetchDataResponse(response) {
    if (response == null) {
        return null;
    }
    if (typeof response === "object") {
        return response;
    }
    if (typeof response === "string") {
        try {
            return JSON.parse(response);
        } catch (error) {
            console.error("Invalid JSON response", error);
            return null;
        }
    }

    return null;
}

/** Guard against unbounded pagination loops when fetching inbox pages for attachments. */
const MAX_CONVERSATION_ATTACHMENT_PAGES = 200;

/**
 * Fetches all attachment metadata for a conversation by paging the inbox attachments API
 * until no lastKey remains. Deduplicates by URL (newest occurrence wins ordering via sort).
 */
export default function useConversationAttachments(appProperties) {
    const { fetchData } = useHttp();

    const fetchAllConversationAttachments = useCallback(
        async ({ integId, fromNumber, toNumber, leftServiceId, contactId }) => {
            if (!appProperties?.osyncId || !integId || !fromNumber || !toNumber || !leftServiceId) {
                throw new Error("Missing required parameters for conversation media");
            }

            let baseUrl =
                `channels/conversation/attachments?integId=${encodeURIComponent(integId)}` +
                `&orgMember=${encodeURIComponent(fromNumber)}` +
                `&orgCustomer=${encodeURIComponent(toNumber)}` +
                `&leftServiceId=${encodeURIComponent(leftServiceId)}`;
            if (contactId) {
                baseUrl += `&contactId=${encodeURIComponent(contactId)}`;
            }

            const mergedByUrl = new Map();
            let lastKey = null;

            for (let pageIndex = 0; pageIndex < MAX_CONVERSATION_ATTACHMENT_PAGES; pageIndex++) {
                let url = baseUrl;
                if (lastKey) {
                    url += `&lastKey=${encodeURIComponent(lastKey)}`;
                }
                const response = await fetchData(url, "GET", null, appProperties);
                const parsed = parseFetchDataResponse(response);
                if (!parsed) {
                    throw new Error("Empty or invalid response");
                }
                if (parsed?.data?.success === false) {
                    throw new Error("Failed to load media");
                }
                const pageAttachments = parsed?.data?.data?.attachments ?? [];
                const pagination = parsed?.data?.data?.pagination;

                for (const att of pageAttachments) {
                    if (att?.url && !mergedByUrl.has(att.url)) {
                        mergedByUrl.set(att.url, att);
                    }
                }

                lastKey = pagination?.lastKey;
                if (lastKey == null || String(lastKey).trim() === "") {
                    break;
                }
            }

            return Array.from(mergedByUrl.values()).sort((a, b) => {
                const ta = (a.messageCreatedTime || "").trim();
                const tb = (b.messageCreatedTime || "").trim();
                const emptyA = ta === "";
                const emptyB = tb === "";
                if (emptyA && emptyB) {
                    return 0;
                }
                if (emptyA) {
                    return 1;
                }
                if (emptyB) {
                    return -1;
                }
                return tb.localeCompare(ta);
            });
        },
        [appProperties, fetchData]
    );

    return { fetchAllConversationAttachments };
}
